import { type } from 'arktype';
import type { TeamSettings, TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';

export const TEAM_TRANSLATABLE_FIELDS = ['name', 'description'] as const;
export type TeamTranslatableField = (typeof TEAM_TRANSLATABLE_FIELDS)[number];
export type TeamTranslations = TranslatableText<TeamTranslatableField>;

const visibleShiftUnitIdsSchema = type('string>=1')
  .array()
  .atLeastLength(1)
  .atMostLength(2000)
  .narrow((ids, ctx) => new Set(ids).size === ids.length || ctx.mustBe('unique shift-unit IDs'));

const teamSettingsSchema = type({
  '+': 'reject',
  'planning?': type({
    '+': 'reject',
    'visibleShiftUnitIds?': visibleShiftUnitIdsSchema,
  }),
});

export const teamQuerySchema = type({
  'name?': 'string',
});

export type TeamQuery = typeof teamQuerySchema.t;

export const createTeamSchema = type({
  '+': 'reject',
  'name?': type('string').configure({
    description: 'Display name of the team. Optional if `translations.en.name` is supplied.',
    examples: ['Reception', 'Night Shift'],
  }),
  'description?': type('string | null').configure({
    description: 'Free-form description. `null` clears the value.',
    examples: ['Reception desk and visitor services.'],
  }),
  'icon?': type('string | null').configure({
    description: 'Material Icons identifier. `null` falls back to the default icon.',
    examples: ['groups'],
  }),
  'color?': type('string | null').configure({
    description: 'CSS accent colour, typically a six-digit hex. `null` clears the colour.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'URL to an illustrative image. `null` clears the image.',
  }),
  strategyId: type('string>=1').configure({
    description:
      "ID of the planning strategy. Required. Used to bootstrap the team's default plan and initial plan version. Plan instances retain standalone planning state.",
    examples: ['strategy_abc123'],
  }),
  'translations?': translatableTextSchema(TEAM_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name` and `description`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({ description: 'Payload for creating a new team.' });

export type CreateTeam = typeof createTeamSchema.t;

export const updateTeamSchema = type({
  '+': 'reject',
  'name?': type('string').configure({
    description: 'Updated display name.',
    examples: ['Reception'],
  }),
  'description?': type('string | null').configure({
    description: 'Updated description. `null` clears the value.',
  }),
  'icon?': type('string | null').configure({
    description: 'Updated Material Icons identifier. `null` clears the icon.',
    examples: ['groups'],
  }),
  'color?': type('string | null').configure({
    description: 'Updated accent colour. `null` clears the colour.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'Updated image URL. `null` clears the image.',
  }),
  'settings?': teamSettingsSchema.configure({
    description: 'Team display settings. Only the documented planning visibility path is accepted.',
  }),
  'translations?': translatableTextSchema(TEAM_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({ description: 'Partial update of a team — fields omitted from the body remain unchanged.' });

export type UpdateTeam = typeof updateTeamSchema.t;

/**
 * Team response type returned by the API.
 */
export interface TeamResponse {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  imageUrl: string | null;
  settings: TeamSettings;
  memberCount?: number;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
  createdAt: string;
  updatedAt: string;
}
